import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SimpleAppointmentService from "../appointments/SimpleAppointmentService";
import PatientInfoService from "../patientInfo/patientInfoService";
import OfficeService from "../office/officeService";
import apiBaseUrl from "../../utils/config";
import "../appointments/modernSimpleAppointment.css";

const simpleAppointmentAPI = new SimpleAppointmentService();

const DEFAULT_NOT_SURE_DOCTOR_ID = 15;

const AppointmentScheduler = ({ preselectedOfficeId = null, patientId }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    patientId: patientId || "",
    officeId: preselectedOfficeId || "",
    doctorId: DEFAULT_NOT_SURE_DOCTOR_ID,
    appointmentDate: "",
    appointmentTime: "",
    notes: "",
    conformation: "Scheduled",
  });

  const [officeName, setOfficeName] = useState("");
  const [availability, setAvailability] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [resolvedPatientId, setResolvedPatientId] = useState(patientId || null);
  const [patientName, setPatientName] = useState("");
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isPatientUser = userRole === "PATIENT";

  const allTimeSlots = availability?.allSlots || [];
  const bookedSlots = availability?.bookedSlots || [];
  const availableSlots = availability?.availableSlots || [];
  const slotCounts = availability?.slotCounts || {};
  const maxPerSlot = availability?.incraments || 0;

  useEffect(() => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      navigate("/login/patient");
      return;
    }

    const role = localStorage.getItem("role")?.replace("ROLE_", "") || "";
    setUserRole(role);

    if (role === "PATIENT") {
      const username = localStorage.getItem("username");

      if (username) {
        const patientService = new PatientInfoService();

        patientService
          .getPatientByUsername(username)
          .then((patient) => {
            const foundPatientId = patient.patientId || patient.patient_id;
            const fullName = `${patient.firstName || ""} ${
              patient.lastName || ""
            }`.trim();

            setResolvedPatientId(foundPatientId);
            setPatientName(fullName || `Patient #${foundPatientId}`);

            setForm((prev) => ({
              ...prev,
              patientId: foundPatientId,
            }));
          })
          .catch((err) => {
            console.error("Failed to load patient:", err);
            alert("Could not load your patient profile.");
          });
      }
    }

    const officeIdToUse =
      preselectedOfficeId || localStorage.getItem("office_id");

    if (officeIdToUse) {
      loadOfficeName(officeIdToUse);

      setForm((prev) => ({
        ...prev,
        officeId: officeIdToUse,
      }));
    }
  }, [navigate, patientId, preselectedOfficeId]);

  useEffect(() => {
    if (!confirmation) return;

    const timer = setTimeout(() => {
      setConfirmation(null);
    }, 10000);

    return () => clearTimeout(timer);
  }, [confirmation]);

  useEffect(() => {
    const listener = (e) => {
      const { inputTarget, value, displayName } = e.data || {};

      if (inputTarget === "selectedPatientId") {
        setResolvedPatientId(value);
        setPatientName(displayName || `Patient #${value}`);

        setForm((prev) => ({
          ...prev,
          patientId: value,
        }));
      }

      if (inputTarget === "officeId") {
        setForm((prev) => ({
          ...prev,
          officeId: value,
          appointmentDate: "",
          appointmentTime: "",
        }));

        setOfficeName(displayName || `Office #${value}`);
        setAvailability(null);
        localStorage.setItem("office_id", value);
      }
    };

    window.addEventListener("message", listener);

    return () => window.removeEventListener("message", listener);
  }, []);

  const selectedDateLabel = useMemo(() => {
    if (!form.appointmentDate) return "";

    try {
      const date = new Date(`${form.appointmentDate}T00:00:00`);
      return date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return form.appointmentDate;
    }
  }, [form.appointmentDate]);

  const loadOfficeName = async (officeId) => {
    try {
      const officeService = new OfficeService();
      const office = await officeService.getOfficeById(officeId);

      setOfficeName(office.officeName || `Office #${officeId}`);
    } catch (err) {
      console.error("Failed to load office name:", err);
      setOfficeName(`Office #${officeId}`);
    }
  };

  const formatTime12Hour = (timeValue) => {
    if (!timeValue) return "";

    const parts = String(timeValue).split(":");
    let hour = parseInt(parts[0], 10);
    const minute = parts[1] || "00";

    if (Number.isNaN(hour)) {
      return timeValue;
    }

    const suffix = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;

    if (hour === 0) {
      hour = 12;
    }

    return `${hour}:${minute} ${suffix}`;
  };

  const fetchOfficeAvailability = async (officeId, date) => {
    if (!officeId || !date) {
      return;
    }

    try {
      setLoadingTimes(true);
      setAvailability(null);

      const response = await simpleAppointmentAPI.getOfficeAvailability(
        officeId,
        date
      );

      setAvailability(response || null);
    } catch (err) {
      console.error("Error loading office availability:", err);
      setAvailability(null);
      alert(
        err.message || "Could not load available times for this office/date."
      );
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDateChange = async (date) => {
    setForm((prev) => ({
      ...prev,
      appointmentDate: date,
      appointmentTime: "",
    }));

    if (!form.officeId) {
      alert("Please select an office before choosing a date.");
      return;
    }

    await fetchOfficeAvailability(form.officeId, date);
  };

  const handleTimeSelect = (time) => {
    handleChange("appointmentTime", time);
  };

  const clearForm = () => {
    setForm((prev) => ({
      ...prev,
      doctorId: DEFAULT_NOT_SURE_DOCTOR_ID,
      appointmentDate: "",
      appointmentTime: "",
      notes: "",
      conformation: "Scheduled",
    }));

    setAvailability(null);
  };

  const handleSubmitAppointment = async () => {
    const finalPatientId = resolvedPatientId || form.patientId;

    if (!finalPatientId) {
      alert("Patient is not selected.");
      return;
    }

    if (!form.officeId) {
      alert("Please select an office.");
      return;
    }

    if (!form.appointmentDate) {
      alert("Please select an appointment date.");
      return;
    }

    if (!form.appointmentTime) {
      alert("Please select an appointment time.");
      return;
    }

    const finalData = {
      patientId: parseInt(finalPatientId, 10),
      officeId: parseInt(form.officeId, 10),
      doctorId: DEFAULT_NOT_SURE_DOCTOR_ID,
      appointmentDate: form.appointmentDate,
      appointmentTime: form.appointmentTime,
      notes: form.notes,
      conformation: "Scheduled",
    };

    try {
      setSubmitting(true);

      const result = await simpleAppointmentAPI.addSimpleAppointment(finalData);

      if (result?.success) {
        setConfirmation(
          "Appointment successfully scheduled. A confirmation email will be sent if email permissions allow it."
        );
        clearForm();
      } else {
        alert(
          "Failed to add appointment: " +
            (result?.message || "Unknown error.")
        );
      }
    } catch (error) {
      console.error("Error adding simple appointment:", error);
      alert(error.message || "An error occurred while adding the appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  const openPatientSearch = () => {
    const popupUrl = `/search-popup?type=Patient&inputTarget=selectedPatientId&url=${encodeURIComponent(
      apiBaseUrl + "/patientinfo/search/all"
    )}`;

    window.open(popupUrl, "_blank", "width=700,height=650");
  };

  const openOfficeSearch = () => {
    window.open("/office-search", "_blank", "width=700,height=650");
  };

  const changeOffice = () => {
    setForm((prev) => ({
      ...prev,
      officeId: "",
      appointmentDate: "",
      appointmentTime: "",
    }));

    setOfficeName("");
    setAvailability(null);
    localStorage.removeItem("office_id");
  };

  const canSubmit =
    !submitting &&
    resolvedPatientId &&
    form.officeId &&
    form.appointmentDate &&
    form.appointmentTime;

  return (
    <div className="simple-appt-page">
      <div className="simple-appt-card">
        <div className="simple-appt-header">
          <div>
            <p className="simple-appt-kicker">New appointment</p>
            <h2>Schedule an Appointment</h2>
            <p>
              Select an office, date, and available 30-minute appointment time.
            </p>
          </div>
        </div>

        {confirmation && (
          <div className="simple-appt-success">
            <div>
              <strong>Success</strong>
              <p>{confirmation}</p>
            </div>

            <button type="button" onClick={() => setConfirmation(null)}>
              ×
            </button>
          </div>
        )}

        {!isPatientUser && (
          <div className="simple-appt-section">
            <div className="simple-appt-section-title">
              <span>1</span>
              <div>
                <h3>Patient</h3>
                <p>Choose who this appointment is for.</p>
              </div>
            </div>

            <div className="simple-appt-selection-row">
              <button
                type="button"
                className="simple-appt-secondary-btn"
                onClick={openPatientSearch}
              >
                Search Patient
              </button>

              <div className="simple-appt-selected-box">
                <small>Selected patient</small>
                <strong>{patientName || "No patient selected"}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="simple-appt-section">
          <div className="simple-appt-section-title">
            <span>{isPatientUser ? "1" : "2"}</span>
            <div>
              <h3>Office and Date</h3>
              <p>Choose where and when you want the appointment.</p>
            </div>
          </div>

          <div className="simple-appt-grid two">
            <div>
              <label>Office</label>

              {form.officeId ? (
                <div className="simple-appt-office-card">
                  <div>
                    <small>Selected office</small>
                    <strong>{officeName || `Office #${form.officeId}`}</strong>
                  </div>

                  <button
                    type="button"
                    className="simple-appt-link-btn"
                    onClick={changeOffice}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="simple-appt-secondary-btn"
                  onClick={openOfficeSearch}
                >
                  Select Office
                </button>
              )}
            </div>

            <div>
              <label>Date</label>
              <input
                type="date"
                value={form.appointmentDate}
                onChange={(e) => handleDateChange(e.target.value)}
                disabled={!form.officeId}
              />

              {!form.officeId && (
                <p className="simple-appt-help">Select an office first.</p>
              )}
            </div>
          </div>

          {form.officeId && form.appointmentDate && availability && (
            <div
              className={
                isPatientUser
                  ? "simple-appt-hours-summary patient-view"
                  : "simple-appt-hours-summary"
              }
            >
              <div>
                <small>Date</small>
                <strong>{selectedDateLabel}</strong>
              </div>

              <div>
                <small>Office hours</small>
                <strong>
                  {availability.openHour && availability.closeHour
                    ? `${formatTime12Hour(
                        availability.openHour
                      )} - ${formatTime12Hour(availability.closeHour)}`
                    : "Not available"}
                </strong>
              </div>

              {!isPatientUser && (
                <>
                  <div>
                    <small>Appointments per 30 min</small>
                    <strong>{maxPerSlot || "N/A"}</strong>
                  </div>

                  <div>
                    <small>Open slots</small>
                    <strong>{availableSlots.length}</strong>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="simple-appt-section">
          <div className="simple-appt-section-title">
            <span>{isPatientUser ? "2" : "3"}</span>
            <div>
              <h3>Choose Time</h3>
              <p>Select one available time slot.</p>
            </div>
          </div>

          {!form.officeId && (
            <div className="simple-appt-empty">
              Select an office to see appointment times.
            </div>
          )}

          {form.officeId && !form.appointmentDate && (
            <div className="simple-appt-empty">
              Select a date to load available times.
            </div>
          )}

          {loadingTimes && (
            <div className="simple-appt-empty">Loading available times...</div>
          )}

          {!loadingTimes &&
            form.officeId &&
            form.appointmentDate &&
            availability?.message &&
            allTimeSlots.length === 0 && (
              <div className="simple-appt-empty">{availability.message}</div>
            )}

          {!loadingTimes && allTimeSlots.length > 0 && (
            <div className="simple-appt-time-grid">
              {allTimeSlots.map((time) => {
                const isBooked = bookedSlots.includes(time);
                const isSelected = time === form.appointmentTime;
                const count = slotCounts[time] || 0;

                return (
                  <button
                    key={time}
                    type="button"
                    disabled={isBooked}
                    className={[
                      "simple-appt-time-btn",
                      isBooked ? "is-booked" : "is-available",
                      isSelected ? "is-selected" : "",
                      isPatientUser ? "patient-time-btn" : "",
                    ].join(" ")}
                    onClick={() => !isBooked && handleTimeSelect(time)}
                    title={
                      isPatientUser
                        ? isBooked
                          ? `${formatTime12Hour(time)} unavailable`
                          : `${formatTime12Hour(time)} available`
                        : `${formatTime12Hour(
                            time
                          )} - ${count}/${maxPerSlot} booked`
                    }
                  >
                    <strong>{formatTime12Hour(time)}</strong>

                    {isPatientUser ? (
                      <small>{isBooked ? "Unavailable" : "Available"}</small>
                    ) : (
                      <small>
                        {isBooked ? "Full" : `${count}/${maxPerSlot} booked`}
                      </small>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="simple-appt-selected-time">
            <small>Selected time</small>
            <strong>
              {form.appointmentTime
                ? formatTime12Hour(form.appointmentTime)
                : "No time selected"}
            </strong>
          </div>
        </div>

        <div className="simple-appt-section">
          <div className="simple-appt-section-title">
            <span>{isPatientUser ? "3" : "4"}</span>
            <div>
              <h3>Notes</h3>
              <p>Add optional details or a doctor preference.</p>
            </div>
          </div>

          <label>Notes / Doctor Preference</label>
          <textarea
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Optional notes. You may write a doctor preference here, but it is not guaranteed."
          />
        </div>

        <div className="simple-appt-footer">
          <div>
            <small>Appointment summary</small>
            <p>
              {!isPatientUser && `${patientName || "No patient"} · `}
              {officeName || "No office"} · {selectedDateLabel || "No date"} ·{" "}
              {form.appointmentTime
                ? formatTime12Hour(form.appointmentTime)
                : "No time"}
            </p>
          </div>

          <button
            className="simple-appt-primary-btn"
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmitAppointment}
          >
            {submitting ? "Scheduling..." : "Schedule Appointment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentScheduler;